import React, { useEffect, useRef, useState } from "react";
import { hasWebpVersion, getWebpUrl, getFallbackUrl } from "./webpUtils";
import { FeatureFlags, getFeatureFlag } from "../../utilities/featureFlags";
import classNames from "classnames";
import wait from "../../utilities/wait";

export default function Picture(props) {
    const {src, alt, dataTestId, pictureClassName, id = '', adobeWait = 0, placeholderSrc = '', fallbackSrc = '', imgRef: imgReference, ...imgAttr} = props;
    const enableWebp = getFeatureFlag(FeatureFlags.PREFER_WEBP_IMAGES);
    const [webpExists, setWebpExists] = useState(true);
    const placeholderImgSrc = getFallbackUrl(placeholderSrc),
        placeholderWebpSrc = getWebpUrl(placeholderSrc);
    const sourceRef = useRef<HTMLSourceElement>();
    const _imgRef = useRef<HTMLImageElement>();
    const imgRef = imgReference || _imgRef;
    const timeoutRef = useRef(0);

    const [{ imgSrc, webpSrc, showAdobeIds }, setImages] = useState({
        imgSrc: adobeWait > 0 ? placeholderImgSrc : getFallbackUrl(src),
        webpSrc: adobeWait > 0 ? placeholderWebpSrc : getWebpUrl(src),
        showAdobeIds: adobeWait > 0,
    });

    useEffect(() => {
        setImages({
            imgSrc: adobeWait > 0 ? placeholderImgSrc : getFallbackUrl(src),
            webpSrc: adobeWait > 0 ? placeholderWebpSrc : getWebpUrl(src),
            showAdobeIds: adobeWait > 0,
        })
    }, [src]);

    useEffect(() => {
        async function loadImages() {
            if (adobeWait > 0) {
                await wait(adobeWait);
                setImages((props) => {
                    const sourceSrc = sourceRef?.current?.srcset;
                    const imgSrc = imgRef?.current?.src;
                    return {
                        imgSrc: (imgSrc && imgSrc !== props.imgSrc) ? imgSrc : getFallbackUrl(src),
                        webpSrc: (sourceSrc && sourceSrc !== props.webpSrc) ? sourceSrc : getWebpUrl(src),
                        showAdobeIds: false,
                    };
                });
            }
            timeoutRef.current = window.setTimeout(() => {
                if (!imgRef.current || !imgRef.current?.complete || imgRef.current?.naturalWidth === 0) {
                    handleError(true);
                }
            }, 3000);
        }
        loadImages();
    }, []);

    if (!enableWebp || !hasWebpVersion(src)) {
        if (pictureClassName) {
            return (
                <picture className={classNames(pictureClassName)}>
                    <img ref={imgRef} src={imgSrc} alt={alt} {...imgAttr} id={getAdobeId('img')} data-test-id={dataTestId && `${dataTestId}_img`}/>
                </picture>
            );
        }
        return (
            <img ref={imgRef} src={imgSrc} alt={alt} {...imgAttr} id={getAdobeId('img')} data-test-id={dataTestId && `${dataTestId}_img`}/>
        );
    }

    function handleError(timeout = false) {
        clearTimeout(timeoutRef.current);
        if (fallbackSrc) {
            setImages((props) => ({ ...props, imgSrc: fallbackSrc }));
            console.error(`${timeout ? 'Timeout' : 'Failed'} to load image ${src}, loading fallback ${fallbackSrc}`);
        }
        setWebpExists(false);
    }

    function onLoad() {
        clearTimeout(timeoutRef.current);
    }

    function getAdobeId(suffix) {
        if (id && showAdobeIds) {
            return `${id}_${suffix}`;
        }
        return null;
    }

    return (
        <picture className={classNames(pictureClassName)} data-test-id={dataTestId}>
            {webpExists && <source ref={sourceRef} srcSet={webpSrc} type="image/webp" id={getAdobeId('source')} onError={() => handleError()} data-test-id={dataTestId && `${dataTestId}_source`}/>}
            <img {...imgAttr} ref={imgRef} src={imgSrc} alt={alt} id={getAdobeId('img')} onError={() => handleError()} onLoad={onLoad} data-test-id={dataTestId && `${dataTestId}_img`}/>
        </picture>
    );
}